import { useSession } from '@/lib/auth';
import { usePlayerPersistenceEnabled } from '@/lib/auth/PlayerPersistenceContext';
import { useAnonymousId } from './useAnonymousId';

/**
 * Provides a single, resolved user ID and identity status
 * Waits for auth and its request token before returning an ID
 *
 * This hook ensures that:
 * 1. We never return both authenticated and anonymous ID for the same user
 * 2. We wait until the matching request token is usable before returning an ID
 * 3. Player queries start with matching identity and request credentials
 * 4. Routes that disable player persistence expose no database identity
 *
 * @returns {
 *   userId: string | null - The persistent player ID, or null while resolving or disabled
 *   isAuthenticated: boolean - Whether the user ID represents an authenticated user
 *   isResolved: boolean - Whether auth has finished resolving, even when persistence is disabled
 * }
 */
export const useAuthId = () => {
  const { user, authResolved, requestsReady } = useSession();
  const anonymousId = useAnonymousId();
  const playerPersistenceEnabled = usePlayerPersistenceEnabled();

  // Auth events expose the new user before the SDK can read their token.
  // Keep player queries disabled until both describe the same principal.
  if (!authResolved || requestsReady === false) {
    return { userId: null, isAuthenticated: false, isResolved: false };
  }

  if (!playerPersistenceEnabled) {
    return { userId: null, isAuthenticated: false, isResolved: true };
  }

  // Auth is resolved - return the appropriate ID
  if (user?.id) {
    return {
      userId: user.id,
      isAuthenticated: true,
      isResolved: true,
    };
  }

  return {
    userId: anonymousId,
    isAuthenticated: false,
    isResolved: anonymousId !== null,
  };
};
